import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { movies } from "@/mocks/movies";
import { filterAndSortMovies, type MovieFilters } from "@/utils/movie";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar from "@/components/feature/FilterBar";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const category = categories.find((item) => item.key === type);
  const [filters, setFilters] = useState<MovieFilters>({ sort: "heat" });
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilters({ sort: "heat" });
    setVisible(PAGE_SIZE);
  }, [type]);

  useSeo({
    title: category ? `${category.name}电影分类 - 黄色大片` : "电影分类 - 黄色大片",
    description: category
      ? `${category.name}电影最新资讯和推荐，${category.desc}为你精选值得一看的${category.name}片。`
      : "浏览各类电影分类，获取最新资讯与推荐。",
    keywords: category ? `${category.name}电影,${category.name}片推荐,黄色大片,电影分类` : "电影分类,黄色大片",
  });

  const result = useMemo(() => filterAndSortMovies(movies, filters), [filters]);
  const shown = result.slice(0, visible);

  if (!category) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-500" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50">未找到该电影分类</h1>
        <p className="mt-2 text-sm text-foreground-400">你访问的分类可能不存在，换个类型看看吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
        >
          <i className="ri-home-4-line text-lg" />
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "电影分类" }, { label: `${category.name}片` }]} />

      <div className="mt-5 flex flex-col gap-4 border-b border-background-200/50 pb-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-400">
            <i className={`${category.icon} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">{category.name}电影</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">{category.desc}</p>
          </div>
        </div>
        <p className="text-sm text-foreground-500">
          共 <span className="font-semibold text-primary-400">{result.length}</span> 部影片
        </p>
      </div>

      <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1">
        {categories.map((item) => (
          <Link
            key={item.key}
            to={`/category/${item.key}`}
            className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              item.key === category.key
                ? "bg-primary-500 text-foreground-950"
                : "bg-background-100 text-foreground-300 hover:bg-background-200/70 hover:text-foreground-50"
            }`}
          >
            <i className={`${item.icon} text-sm`} />
            {item.name}
          </Link>
        ))}
      </div>

      <div className="mt-5">
        <FilterBar filters={filters} onChange={setFilters} />
      </div>

      {result.length === 0 ? (
        <div className="flex flex-col items-center py-24 text-center">
          <i className="ri-inbox-line text-5xl text-foreground-500" />
          <h2 className="mt-5 font-heading text-xl font-bold text-foreground-50">暂无符合条件的电影</h2>
          <p className="mt-2 text-sm text-foreground-400">试着放宽筛选条件，或重置后再看看。</p>
          <button
            type="button"
            onClick={() => setFilters({ sort: "heat" })}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-refresh-line text-lg" />
            重置筛选
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {shown.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          {visible < result.length && (
            <div className="mt-9 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((value) => value + PAGE_SIZE)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/60 px-7 py-3 font-label text-sm uppercase tracking-wide text-foreground-100 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                <i className="ri-add-line text-lg" />
                加载更多（{result.length - visible}）
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}